const PROPOSAL_PATH = "/vision/proposta";

/**
 * Single source of truth for the client-facing proposal URL.
 *
 * Every proposal shares /vision/proposta since PED-12653 — the template is
 * picked by ProposalRoutePage from the fetched payload's own proposal_type,
 * not by the URL (/vision/proposta-smb survives only as an alias for
 * already-sent SMB links). The URL must never depend on how the proposal
 * was generated (manual flow vs automated/Bruna) nor on a backend-stored
 * absolute URL — stored URLs pointed to dead hosts and leaked production
 * links into staging.
 *
 * The base comes from VITE_VISION_PROPOSAL_URL, which historically is set
 * either as a bare host or already suffixed with /vision/proposta, so both
 * shapes are normalized here.
 */
export function getProposalUrl(uuid: string): string {
  const configuredBase = import.meta.env.VITE_VISION_PROPOSAL_URL;
  const base = (
    configuredBase ?? `${window.location.origin}${PROPOSAL_PATH}`
  ).replace(/\/+$/, "");
  const proposalBase = base.endsWith(PROPOSAL_PATH)
    ? base
    : `${base}${PROPOSAL_PATH}`;
  return `${proposalBase}/${uuid}`;
}

/**
 * The uuids that render a mock instead of fetching: the internal
 * design-review previews. Shared by the route (which must not fetch for
 * them) and the page (which picks the mock) so a new preview is added in
 * one place.
 *
 * - `preview` — the SMB mock, a 20–30 position deal.
 * - `preview-ent` — the same mock dressed as Enterprise, for the ENT-only
 *   pricing block.
 * - `preview-small` — the same mock as a 5–10 position deal.
 * - `preview-large` — the same mock as a 35–45 position deal, the only one
 *   past the briefing's 31-position cut (PED-14146).
 */
export const PROPOSAL_PREVIEW_UUIDS = [
  "preview",
  "preview-ent",
  "preview-small",
  "preview-large",
] as const;

export function isPreviewUuid(uuid: string | undefined): boolean {
  return (PROPOSAL_PREVIEW_UUIDS as readonly string[]).includes(uuid ?? "");
}
